import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Guest } from '@/models'
import { neon } from '@/services/neon'

export async function fetchGuests(companyId?: string): Promise<Guest[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  let query = neon
    .from('guests')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (user.role === ROLE_ADMIN) {
    if (companyId) {
      query = query.eq('company_id', companyId)
    }
  } else {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (companyIds.length === 0) {
      return []
    }
    if (companyId) {
      if (!companyIds.includes(companyId)) {
        return []
      }
      query = query.eq('company_id', companyId)
    } else {
      query = query.in('company_id', companyIds)
    }
  }

  const { data, error } = await query
  if (error) {
    throw new Error(error.message)
  }
  return (data as Guest[]) || []
}

export async function fetchGuestById(id: string): Promise<Guest> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await neon
    .from('guests')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  if (error) {
    throw new Error(error.message)
  }

  const guest = data as Guest
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!guest.company_id || !companyIds.includes(guest.company_id)) {
      throw new Error('You do not have permission to view this guest.')
    }
  }

  return guest
}

export async function createGuest(payload: Partial<Guest>): Promise<Guest> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  if (user.role !== ROLE_ADMIN) {
    if (!payload.company_id) {
      throw new Error('Company ID is required.')
    }
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!companyIds.includes(payload.company_id)) {
      throw new Error('You do not have permission to create guests for this company.')
    }
  }

  const { data, error } = await neon
    .from('guests')
    .insert({ ...payload, created_by: user.id })
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }
  return data as Guest
}

export async function updateGuest(id: string, payload: Partial<Guest>): Promise<Guest> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchGuestById(id)
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to update this guest.')
    }
  }

  const { data, error } = await neon
    .from('guests')
    .update(payload)
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) throw new Error('Guest not found or permission revoked — please refresh.')
    throw new Error(error.message)
  }
  return data as Guest
}

export async function deleteGuest(id: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const existing = await fetchGuestById(id)
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!existing.company_id || !companyIds.includes(existing.company_id)) {
      throw new Error('You do not have permission to delete this guest.')
    }
  }

  const { error, data } = await (neon.from('guests') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return
    throw new Error(error.message)
  }
  if (!data) return
}

export async function searchGuests(
  query_: string,
  companyId?: string,
): Promise<Array<{ value: string; label: string }>> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  let q = neon
    .from('guests')
    .select('id, primary_name, contact_number')
    .is('deleted_at', null)
    .ilike('primary_name', `%${query_}%`)

  if (user.role === ROLE_ADMIN) {
    if (companyId) {
      q = q.eq('company_id', companyId)
    }
  } else {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (companyIds.length === 0) {
      return []
    }
    if (companyId) {
      if (!companyIds.includes(companyId)) {
        return []
      }
      q = q.eq('company_id', companyId)
    } else {
      q = q.in('company_id', companyIds)
    }
  }

  const { data, error } = await q.limit(20)
  if (error) {
    throw new Error(error.message)
  }

  return ((data as Array<{ id: string; primary_name: string; contact_number?: string }>) || []).map(
    (g) => ({
      value: g.id,
      label: `${g.primary_name}${g.contact_number ? ` (${g.contact_number})` : ''}`,
    }),
  )
}
